/**
 * FastSave — media downloader
 *
 * Its single web-accessible-resource entry exposes `icons/*`, `js/*` and `js/storyIn.js`
 * to `<all_urls>` with no `use_dynamic_url` — so every icon and script is fetchable from
 * any page at a fixed URL. The 128px icon is the cleanest signal (an image, so no CORS
 * grant needed), and the vendor-named `js/storyIn.js` corroborates.
 *
 * Manifest verified against version 3.8.8.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'pnlphjjfielecalmmjjdhjjninkbjdod';

export const fastsave: ExtensionDefinition = {
  id: ID,
  name: 'FastSave',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Exposes icons/* and js/* to <all_urls> with no dynamic URL. The icon loads without ' +
    'a CORS grant and js/storyIn.js corroborates; detection is conclusive.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load icons/128.png via <img> — exposed to every origin and readable without a ' +
        'CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'icons/128.png'),
    },
    {
      id: 'war-story-in-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch js/storyIn.js — a vendor-named script exposed to every origin, independent ' +
        'of the icon path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/storyIn.js'),
    },
    {
      id: 'war-icon-64',
      kind: 'web-accessible-resource',
      description: 'Load icons/64.png via <img> — a second image path corroborator.',
      weight: 0.5,
      run: () => probeImage(ID, 'icons/64.png'),
    },
  ],
};
